import React, { createContext, useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { identifyUser, resetUser } from '../lib/analytics';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        let mounted = true;

        // Check active session
        const getSession = async () => {
            try {
                const { data: { session }, error } = await supabase.auth.getSession();
                if (error) throw error;
                if (mounted) setUser(session?.user ?? null);
            } catch (err) {
                console.error("Auth Check Error:", err);
            } finally {
                if (mounted) setLoading(false);
            }
        };

        getSession();

        // Listen for auth changes
        const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
            if (mounted) setUser(session?.user ?? null);
            if (mounted) setLoading(false);

            if (session?.user) identifyUser(session.user.id);
            if (event === 'SIGNED_OUT') resetUser();

            // Le lien "mot de passe oublié" connecte l'utilisateur via une session de
            // récupération avant même que la page ne se charge (comportement Supabase).
            // On le redirige explicitement vers le formulaire de changement de mot de
            // passe, quelle que soit la page sur laquelle le lien l'a fait atterrir.
            if (event === 'PASSWORD_RECOVERY') {
                navigate('/reset-password');
            }
        });

        return () => {
            mounted = false;
            subscription.unsubscribe();
        };
    }, [navigate]);

    const signOut = async () => {
        await supabase.auth.signOut();
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-[#F0F9FF]">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <AuthContext.Provider value={{ user, loading, signOut }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    return useContext(AuthContext);
};
